/**
 * Best Friend Instagram Template
 *
 * Página de suscripción para sumarse a la lista de "Close Friends" de Instagram
 * de la creadora. Mobile-first · primera persona · tono cálido y cercano.
 *
 * Brand-aware vía Railway envs (todas opcionales — defaults razonables):
 *   VITE_BESTFRIEND_INSTAGRAM_PRICE_ARS    (precio mensual ARS, default 6999)
 *   VITE_BESTFRIEND_INSTAGRAM_PRICE_USD    (precio mensual USD, default 9.99)
 *   VITE_BESTFRIEND_INSTAGRAM_HERO_TITLE   (título; default "lista de Close Friends")
 *   VITE_BESTFRIEND_INSTAGRAM_HERO_TAGLINE (subtítulo; auto según país)
 *   VITE_BESTFRIEND_INSTAGRAM_INTRO        (párrafo intro)
 *   VITE_BESTFRIEND_INSTAGRAM_QUOTE        (cita personal)
 *   VITE_BESTFRIEND_INSTAGRAM_BG_IMAGE     (URL imagen de fondo)
 *
 * Checkout (membership tipo suscripción mensual, sin email input):
 *   Argentina → MercadoPago via `VITE_MP_CHECKOUT_BASIC_AR` (URL directa MP subscription)
 *   Resto del mundo → `/api/create-subscription-checkout` (Stripe subscription)
 */

import {
  BRAND_SOURCE_KEY,
  BRAND_NAME,
  BRAND_PERSON_NAME,
} from "@/lib/brandConfig";
import { BESTFRIEND_INSTAGRAM_CONFIG } from "@/config/bestFriendInstagramConfig";
import { getMercadoPagoCheckoutBasicArUrl } from "@/lib/mercadopagoArCheckoutUrl";
import { shouldUseMercadoPago } from "@/lib/paymentProviderPolicy";
import { buildStripeSuccessReturnUrl } from "@/lib/muchasGraciasSuccessUrl";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { CheckCircle, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { trackTemplateView, trackTemplateCta } from "@/lib/templateCtaAnalytics";
import { useNavigate } from "react-router-dom";
import { getDatafastAttributionPayload } from "@/lib/datafast";

const SUBSCRIPTION_PRICE_ARS = BESTFRIEND_INSTAGRAM_CONFIG.priceARS || 6999;
const SUBSCRIPTION_PRICE_USD = BESTFRIEND_INSTAGRAM_CONFIG.priceUSD || 9.99;

const HERO_TITLE_TEXT =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_HERO_TITLE as string | undefined)?.trim() ||
  "lista de Close Friends";
const HERO_TAGLINE_OVERRIDE =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_HERO_TAGLINE as string | undefined)?.trim() || "";
const INTRO_TEXT =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_INTRO as string | undefined)?.trim() ||
  "Acceso a mis stories privadas, fotos y momentos que solo comparto con mi círculo más cercano.";
const QUOTE_TEXT =
  (import.meta.env.VITE_BESTFRIEND_INSTAGRAM_QUOTE as string | undefined)?.trim() ||
  "Quería un espacio en Instagram donde mostrar lo que no comparto público. Si querés ser parte, esto es para vos.";

const INSTAGRAM_SVG = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="w-full h-full">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
  </svg>
);

const STAR_SVG = (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden className="w-full h-full">
    <path d="M12 2l2.39 7.36h7.74l-6.27 4.56 2.39 7.36-6.27-4.56-6.27 4.56 2.39-7.36-6.27-4.56h7.74z" />
  </svg>
);

const BENEFITS = [
  { emoji: "✨", text: "Stories privadas que solo ven mis Close Friends" },
  { emoji: "📸", text: "Fotos y momentos que no subo al feed público" },
  { emoji: "💬", text: "Respondo DMs en privado con prioridad" },
  { emoji: "🎁", text: "Sorteos exclusivos solo para Close Friends" },
];

const INCLUDES = [
  "Te agrego a mi lista de Close Friends al instante",
  "Acceso a stories y publicaciones privadas",
  "Mensajes directos con prioridad de respuesta",
  "Podés cancelar cuando querás, sin preguntas",
];

export default function BestFriendInstagramTemplate() {
  const navigate = useNavigate();
  const { isArgentina, country } = useTemplateSelection();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Política unificada: AR también pasa por Stripe. Solo si shouldUseMercadoPago
  // se reactiva el flujo AR original (redirección a MP + label "Pago seguro
  // por Mercado Pago"). El UX queda Stripe-only mientras el master switch
  // esté en false (src/lib/paymentProviderPolicy.ts).
  const useMercadoPago = shouldUseMercadoPago(country);

  const savedBgUrl = BESTFRIEND_INSTAGRAM_CONFIG.backgroundImageUrl || "";

  const heroTagline = HERO_TAGLINE_OVERRIDE ||
    (isArgentina ? "Solo para Argentina · Suscripción mensual" : "Suscripción mensual");

  useEffect(() => {
    trackTemplateView("bestfriend_ig", "BestFriendInstagramTemplate", {
      country,
      is_argentina: isArgentina === true,
    });
  }, [country, isArgentina]);

  const handleSubscribe = async () => {
    setError(null);
    setIsLoading(true);
    trackTemplateCta({
      prefix: "bestfriend_ig",
      template: "BestFriendInstagramTemplate",
      action: "subscribe",
      plan: "basic",
      flowName: "bestfriend_instagram",
      extra: {
        country,
        gateway: useMercadoPago ? "mercadopago" : "stripe",
      },
    });
    try {
      if (useMercadoPago) {
        const mpUrl = getMercadoPagoCheckoutBasicArUrl();
        if (!mpUrl) {
          throw new Error("El checkout de Mercado Pago no está configurado. Intentá de nuevo más tarde.");
        }
        window.location.href = mpUrl;
        return;
      }

      const amountCents = Math.round(SUBSCRIPTION_PRICE_USD * 100);
      const productName = `Close Friends de Instagram — ${BRAND_PERSON_NAME}`;

      const response = await fetch("/api/create-subscription-checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            ...getDatafastAttributionPayload(),
          amount: amountCents,
          currency: "usd",
          productName,
          successUrl: buildStripeSuccessReturnUrl(window.location.origin),
          cancelUrl: window.location.href,
          country,
          metadata: {
            plan_tier: "basic",
            flow: "bestfriend_instagram",
            source: BRAND_SOURCE_KEY,
            country,
          },
        }),
      });

      const data = (await response.json()) as {
        url?: string;
        code?: string;
        error?: string;
      };

      if (data.code === "AR_MUST_USE_MERCADOPAGO" && useMercadoPago) {
        const mpUrl = getMercadoPagoCheckoutBasicArUrl();
        if (mpUrl) {
          window.location.href = mpUrl;
          return;
        }
      }

      if (!response.ok || !data.url) {
        throw new Error(data.error || "Error al crear el checkout.");
      }

      window.location.href = data.url;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Error de conexión. Intentá de nuevo.";
      setError(msg);
      setIsLoading(false);
      trackTemplateCta({
        prefix: "bestfriend_ig",
        template: "BestFriendInstagramTemplate",
        action: "subscribe_error",
        isClick: false,
        flowName: "bestfriend_instagram",
        extra: { country, error: msg.slice(0, 140) },
      });
    }
  };

  const bgStyle = savedBgUrl
    ? { backgroundImage: `url(${savedBgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }
    : {
        background:
          "linear-gradient(160deg, #fef3f9 0%, #fce4ec 35%, #f3e5f5 70%, #fff1e6 100%)",
      };

  return (
    <div className="min-h-screen w-full antialiased" style={bgStyle}>
      {savedBgUrl && <div className="fixed inset-0 bg-white/60 pointer-events-none" />}

      <div className="relative min-h-screen flex flex-col">

        {/* ── Header ─────────────────────────────────────────────── */}
        <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-pink-100/80">
          <div className="max-w-lg mx-auto px-4 h-14 flex items-center justify-between">
            <button
              onClick={() => navigate("/")}
              aria-label="Volver al inicio"
              className="flex items-center gap-1.5 text-gray-500 hover:text-gray-800 transition-colors text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Volver
            </button>

            <div className="flex items-center gap-2">
              <span className="inline-flex w-5 h-5 items-center justify-center rounded-md bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 text-white p-0.5">
                <span className="block w-full h-full">{INSTAGRAM_SVG}</span>
              </span>
              <span className="text-sm font-semibold text-gray-800">Mis Close Friends</span>
            </div>

            <div className="w-14" />
          </div>
        </header>

        {/* ── Contenido principal ─────────────────────────────────── */}
        <main className="flex-1 max-w-lg mx-auto w-full px-4 py-8 space-y-8">

          {/* Hero */}
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-pink-200/60 p-4">
                  <span className="text-white">{INSTAGRAM_SVG}</span>
                </div>
                <span className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-emerald-400 flex items-center justify-center shadow-md">
                  <span className="w-3.5 h-3.5 text-white">{STAR_SVG}</span>
                </span>
              </div>
            </div>

            <div className="space-y-3">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 leading-tight tracking-tight">
                Sumate a mi<br />
                <span className="bg-gradient-to-r from-pink-500 via-rose-500 to-purple-600 bg-clip-text text-transparent">
                  {HERO_TITLE_TEXT}
                </span>
              </h1>
              <p className="text-gray-500 text-sm font-medium">
                {heroTagline}
              </p>
            </div>

            <p className="text-gray-600 text-base sm:text-lg leading-relaxed max-w-sm mx-auto">
              {INTRO_TEXT}{" "}
              <strong className="text-gray-800 font-semibold">Sin algoritmo, sin público.</strong>{" "}
              Solo vos y mi círculo más cercano.
            </p>
          </div>

          {/* Beneficios */}
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-pink-100 p-5 space-y-4 shadow-sm">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
              ¿Qué vas a recibir?
            </p>
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="text-xl leading-none mt-0.5 flex-shrink-0">{b.emoji}</span>
                <span className="text-gray-700 text-sm sm:text-base leading-snug">{b.text}</span>
              </div>
            ))}
          </div>

          {/* Quote */}
          <blockquote className="bg-white/70 backdrop-blur-sm border-l-4 border-pink-500 rounded-r-2xl px-5 py-4">
            <p className="text-gray-600 text-sm sm:text-base italic leading-relaxed">
              &quot;{QUOTE_TEXT}&quot;
            </p>
            <footer className="mt-2 bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent font-semibold text-sm">
              — {BRAND_PERSON_NAME}
            </footer>
          </blockquote>

          {/* ── Card de suscripción ──────────────────────────────── */}
          <div className="bg-white rounded-3xl shadow-xl shadow-pink-100/60 border border-pink-50 overflow-hidden">

            {/* Banner precio */}
            <div className="bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 px-6 py-5 text-center">
              <p className="text-white/95 text-sm font-semibold mb-1">
                ¿Cuánto cuesta unirse?
              </p>
              {isArgentina ? (
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-white">
                    ${SUBSCRIPTION_PRICE_ARS.toLocaleString("es-AR")}
                  </span>
                  <span className="text-white/90 text-sm">ARS por mes</span>
                </div>
              ) : (
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold text-white">
                    ${SUBSCRIPTION_PRICE_USD.toFixed(2)}
                  </span>
                  <span className="text-white/90 text-sm">USD por mes</span>
                </div>
              )}
              <p className="text-white/80 text-xs mt-1.5">
                Cancelás cuando querés — sin preguntas, sin drama
              </p>
            </div>

            <div className="p-6 space-y-5">

              {/* Incluye */}
              <div className="space-y-2.5">
                {INCLUDES.map((item, i) => (
                  <div key={i} className="flex items-start gap-2.5">
                    <CheckCircle className="w-4 h-4 text-pink-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700 text-sm leading-snug">{item}</span>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-100" />

              {/* Error */}
              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-xl px-4 py-3">
                  <p className="text-red-600 text-sm font-medium">{error}</p>
                </div>
              )}

              {/* CTA */}
              <button
                onClick={handleSubscribe}
                disabled={isLoading}
                className="w-full bg-gradient-to-tr from-pink-500 via-rose-500 to-purple-600 hover:opacity-95 active:scale-[0.98] text-white font-bold text-base py-4 px-6 rounded-2xl transition-all flex items-center justify-center gap-2.5 shadow-lg shadow-pink-200/60 disabled:opacity-50 disabled:cursor-not-allowed min-h-[56px]"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <span className="text-xl leading-none">💖</span>
                    ¡Quiero ser Close Friend!
                  </>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center leading-relaxed">
                {isArgentina
                  ? "Vas a ir a Mercado Pago para pagar de forma segura. Después te agrego a mi lista de Close Friends de Instagram."
                  : "Vas a ir a Stripe para pagar de forma segura. Después te agrego a mi lista de Close Friends de Instagram."}
              </p>
            </div>
          </div>

          {/* Badges de confianza */}
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { icon: "🔒", label: useMercadoPago ? "Pago seguro por Mercado Pago" : "Pago seguro por Stripe" },
              { icon: "🔄", label: "Cancelás cuando querés" },
              { icon: "⚡", label: "Acceso a Close Friends el mismo día" },
            ].map((b, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1.5 bg-white/80 border border-pink-100 text-gray-600 text-xs font-medium px-3 py-1.5 rounded-full"
              >
                <span>{b.icon}</span>
                {b.label}
              </span>
            ))}
          </div>
        </main>

        {/* ── Footer ─────────────────────────────────────────────── */}
        <footer className="max-w-lg mx-auto w-full px-4 pb-8 pt-4 text-center space-y-2">
          <button
            onClick={() => navigate("/")}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            ← Volver al inicio
          </button>
          <p className="text-xs text-gray-300">
            © {new Date().getFullYear()} {BRAND_PERSON_NAME || BRAND_NAME}. Todos los derechos reservados.
          </p>
        </footer>

      </div>
    </div>
  );
}
